// Iter 306/308 — who is "Accounting" (QuickBooks queue), who the commissions
// admins are (inquiry notifications) and the payment-method list offered in
// the Record-payment dialog. Super admin only.
import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DEFAULT_PAYMENT_METHODS } from "@/components/commissions/PaymentDialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function WorkflowConfigDialog({ onClose }) {
  const [staff, setStaff] = useState(null);
  const [acc, setAcc] = useState([]);
  const [adm, setAdm] = useState([]);
  const [methods, setMethods] = useState(DEFAULT_PAYMENT_METHODS);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    Promise.all([api.get("/commissions/workflow/staff-options"), api.get("/commissions/workflow/config")])
      .then(([s, c]) => {
        setStaff(s.data.items); setAcc(c.data.accounting_user_ids || []); setAdm(c.data.commissions_admin_user_ids || []);
        if (c.data.payment_methods?.length) setMethods(c.data.payment_methods);
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load"));
  }, []);
  const save = async () => {
    const clean = methods.map((m) => m.trim()).filter(Boolean);
    if (!clean.length) { toast.error("Keep at least one payment method"); return; }
    setBusy(true);
    try {
      await api.put("/commissions/workflow/config", { accounting_user_ids: acc, commissions_admin_user_ids: adm, payment_methods: clean });
      toast.success("Commissions settings saved"); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); } finally { setBusy(false); }
  };
  const toggle = (list, set, id) => set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="workflow-config-dialog">
        <DialogHeader>
          <DialogTitle>Commissions workflow settings</DialogTitle>
          <DialogDescription>
            <b>Accounting</b> receives every accepted pro forma as a QuickBooks task. <b>Commissions admins</b> are notified
            (with the partner&apos;s Partner Success manager and all super admins) when a partner has a question or requests a review.
            <b> Payment methods</b> are the choices offered when recording a payment.
          </DialogDescription>
        </DialogHeader>
        {!staff ? <div className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></div> : (
          <div className="grid max-h-[60vh] grid-cols-2 gap-4 overflow-y-auto text-sm">
            {[["Accounting (QuickBooks queue)", acc, setAcc, "accounting"], ["Commissions admins (inquiries)", adm, setAdm, "admins"]].map(([title, list, set, tid]) => (
              <div key={tid}>
                <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-slate-500">{title} · {list.length}</div>
                <div className="space-y-1 rounded border border-slate-200 p-2">
                  {staff.map((u) => (
                    <label key={u.user_id} className="flex items-center gap-2 text-xs" data-testid={`workflow-${tid}-${u.user_id}`}>
                      <input type="checkbox" checked={list.includes(u.user_id)} onChange={() => toggle(list, set, u.user_id)} />
                      <span className="truncate">{u.name || u.email}</span>
                      <span className="ml-auto truncate text-[10px] text-slate-400">{u.team || u.role}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <PaymentMethodsEditor methods={methods} setMethods={setMethods} />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="workflow-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || !staff} data-testid="workflow-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PaymentMethodsEditor({ methods, setMethods }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim().replace(/\s+/g, " ");
    if (!v) return;
    if (methods.some((m) => m.toLowerCase() === v.toLowerCase())) { toast.error("Already in the list"); return; }
    setMethods([...methods, v]); setDraft("");
  };
  const move = (i, d) => { const n = [...methods]; const [x] = n.splice(i, 1); n.splice(i + d, 0, x); setMethods(n); };
  return (
    <div className="col-span-2" data-testid="workflow-payment-methods">
      <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-slate-500">Payment methods (Record-payment dropdown) · {methods.length}</div>
      <div className="rounded border border-slate-200 p-2">
        <ul className="space-y-1">
          {methods.map((m, i) => (
            <li key={m} className="flex items-center gap-1 text-xs" data-testid={`workflow-method-${m.toLowerCase().replace(/\s+/g, "-")}`}>
              <span className="flex-1 truncate rounded bg-slate-50 px-2 py-1">{m}</span>
              <Button variant="ghost" size="icon" className="h-6 w-6" disabled={i === 0} onClick={() => move(i, -1)} title="Move up" data-testid={`workflow-method-up-${i}`}><ArrowUp className="h-3 w-3" /></Button>
              <Button variant="ghost" size="icon" className="h-6 w-6" disabled={i === methods.length - 1} onClick={() => move(i, 1)} title="Move down" data-testid={`workflow-method-down-${i}`}><ArrowDown className="h-3 w-3" /></Button>
              <Button variant="ghost" size="icon" className="h-6 w-6 text-rose-600" disabled={methods.length === 1} onClick={() => setMethods(methods.filter((_, j) => j !== i))} title="Remove" data-testid={`workflow-method-remove-${i}`}><X className="h-3 w-3" /></Button>
            </li>
          ))}
        </ul>
        <div className="mt-2 flex gap-2">
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
                 placeholder="Add a method, e.g. Wire transfer, Credit note" className="h-8 text-xs" maxLength={40} data-testid="workflow-method-input" />
          <Button variant="outline" size="sm" className="h-8" onClick={add} disabled={!draft.trim()} data-testid="workflow-method-add"><Plus className="h-3.5 w-3.5" /> Add</Button>
        </div>
        <div className="mt-1 text-[11px] text-slate-500">Payments already recorded keep the method they were saved with, even if you remove it here.</div>
      </div>
    </div>
  );
}
